import { computed, defineComponent, onBeforeUnmount, ref, watch } from 'vue';
import type { RouteLocationNormalizedWithState } from 'vue-router';

import { MODALS } from '~/config/modals';

import { useServiceContainer, useStoreContainer } from '~/container';

import { RootState } from '~/modules/core/states';
import { LegacyCoreRouteNames } from '~/router/names/core';

export default defineComponent({
    setup() {
        const { EventBusService, BodyClassService, ModalService } = useServiceContainer();
        const {
            StateResolveStore,
            RouteStore,
            UserStoreRefs,
            ConfigStore,
            VisitStoreRefs,
            AppStoreRefs,
            BodyClassStore,
        } = useStoreContainer();
        const { isAuthenticated: userIsAuthenticated } = UserStoreRefs;
        const { frontendConfiguration } = ConfigStore;
        const { currentVisit } = VisitStoreRefs;
        const { loadingState } = AppStoreRefs;

        const visitLoaded = ref(false);
        const isDev = import.meta.env.DEV && import.meta.env.VITE_DEBUG_BAR_ENABLED === 'true';
        const route = {
            fullPath: '/',
            path: '/',
            query: {},
            hash: '',
            name: LegacyCoreRouteNames.RootPage,
            params: {},
            matched: [
                {
                    path: '/',
                    name: LegacyCoreRouteNames.RootPage,
                    meta: {
                        state: RootState,
                    },
                },
            ],
            meta: {
                state: RootState,
            },
            href: '/',
        } as unknown as RouteLocationNormalizedWithState;

        RouteStore.setRoute(route);

        useServiceContainer('StateResolveService').resolveStates(route);

        const resolved = computed(() => !!StateResolveStore.getStateStatus(RootState)?.finished);

        onBeforeUnmount(() => {
            EventBusService.clearAll();
            removeMultiVariateModalEventListener();
        });

        watch(
            () => BodyClassStore.getCssClasses,
            (value) => BodyClassService.setClassList(value),
            { deep: true, immediate: true },
        );

        watch(
            () => currentVisit.value,
            () => {
                if (!currentVisit.value) {
                    return;
                }

                visitLoaded.value = true;
            },
            { immediate: true },
        );

        watch(
            () => visitLoaded.value,
            () => {
                if (!visitLoaded.value) {
                    return;
                }

                addMultiVariateModalEventListeners();
            },
            { immediate: true },
        );

        function addMultiVariateModalEventListeners() {
            document.addEventListener('keydown', openMultiVariateModal);
        }

        function removeMultiVariateModalEventListener() {
            document.removeEventListener('keydown', openMultiVariateModal);
        }

        function openMultiVariateModal(event: KeyboardEvent) {
            // ALT + CTRL + M
            if (event.ctrlKey && event.altKey && event.code === 'KeyM') {
                event.preventDefault();

                return ModalService.open({
                    id: MODALS.etMultiVariateSelectorModal,
                    size: 'lg',
                });
            }
        }

        return {
            isDev,
            resolved,
            userIsAuthenticated,
            config: frontendConfiguration,
            visit: currentVisit,
            loadingState,
        };
    },
});
